import { Controller, useForm, useController, useWatch } from 'react-hook-form';
import { useSnapshot } from 'valtio';
import {
  Box,
  Button,
  ButtonGroup,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Icon,
  Input,
  Skeleton,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { AsyncSelect, MultiValue, Select, SingleValue } from 'chakra-react-select';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { AlertCircleIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import {
  FormContentContainer,
  FormFooterContainer,
  FormMainContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';
import { quickReferralModalState } from '@duke-hq/app-provider-web/tenant/tenantState/QuickReferralState';
import { djs } from '@duke-hq/djs';
import {
  CategoryOptionsInterface,
  ContractTypes,
  ContractTypesInterface,
  CourseSegment,
  CourseSegmentOptions,
  CourseSegmentOptionsInterface,
  getCategoryOptions,
  WorkSchedule,
} from '@duke-hq/shared-constants/enrolments';
import { useEffect, useMemo, useCallback } from 'react';
import { EnrolmentSettingsDataTypes } from './types';
import { useAuthTenant } from '@duke-hq/app-provider-web/tenant/useAuthTenant';
import { useTenantContext } from '@duke-hq/app-provider-web/tenant/TenantContext';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { Option } from '../../../components/Option';
import { useDebounceCallback } from 'usehooks-ts';
import {
  ConsultantRoleSchema,
  SiteOption,
  ConsultantOption,
} from '@duke-hq/svc-api/provider/util/consultants';
import { isDefined } from '@duke-hq/lib';
import { CourseKindSchema } from '@duke-hq/svc-api-db/schemas/enums/CourseKind.schema';
import { StatesSchema } from '@duke-hq/svc-api-db/schemas/enums/States.schema';
import { getCourseDatesFromAdminCourseSchedule, toUtcStartOfDay } from '@duke-hq/lib/dates';
import { courseHasSemesterTwo } from '@duke-hq/shared-constants/course';
import { ParticipantExistsAlert } from './ParticipantExistsAlert';

export function EnrolmentSettings({
  goToNextStep,
  goToPrevStep,
}: {
  goToNextStep: () => void;
  goToPrevStep: () => void;
}) {
  const auth = useAuthTenant();
  const tenantCtx = useTenantContext();
  const quickReferralModalSnap = useSnapshot(quickReferralModalState);
  const { courseData } = quickReferralModalSnap;

  const { data: course, isLoading } = trpcTenant.courses.courseSchedule.useQuery(
    quickReferralModalSnap.courseData.id,
  );

  const { data: sitesList, isFetching: isSitesLoading } =
    trpcTenant.locations.listUserSiteAccess.useQuery({
      type: 'site',
    });

  const {
    handleSubmit,
    register,
    control,
    getValues,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<EnrolmentSettingsDataTypes>({
    defaultValues: {
      ...quickReferralModalSnap.enrolmentSettingsData,
      start_at_date: djs(quickReferralModalSnap.enrolmentSettingsData.start_at_date).isValid()
        ? djs(quickReferralModalSnap.enrolmentSettingsData.start_at_date).format('YYYY-MM-DD')
        : djs().format('YYYY-MM-DD'),
      course_segment: quickReferralModalSnap.enrolmentSettingsData.course_segment,
      enrolment_contract_type: quickReferralModalSnap.enrolmentSettingsData.enrolment_contract_type,
      contract_category: quickReferralModalSnap.enrolmentSettingsData.contract_category,
      selected_consultant: quickReferralModalSnap.enrolmentSettingsData.selected_consultant
        .consultant_id
        ? {
            consultant_id:
              quickReferralModalSnap.enrolmentSettingsData.selected_consultant.consultant_id,
            consultant_name:
              quickReferralModalSnap.enrolmentSettingsData.selected_consultant.consultant_name,
          }
        : {
            consultant_id: auth.user.id,
            consultant_name: `${auth.user.first_name} ${auth.user.last_name} (myself)`,
          },
    },
  });

  const today = djs();
  const [work_schedule, start_at_date, selected_site, enrolment_contract_type, course_segment] =
    useWatch({
      control,
      name: [
        'work_schedule',
        'start_at_date',
        'selected_site',
        'enrolment_contract_type',
        'course_segment',
        'contract_category',
      ],
    });

  const { data: consultantsInSite, isFetching: isConsultantsLoading } =
    trpcTenant.locations.getConsultantsInSiteLocation.useQuery(
      { siteLocationIds: [selected_site?.site_id] },
      {
        enabled:
          !!selected_site.site_id &&
          tenantCtx?.permissions.includes('canCreateReferralsForTeamMembers'),
        initialData: [],
      },
    );

  const {
    field: { value: contractType, onChange: onContractTypeChange },
  } = useController({
    name: 'enrolment_contract_type',
    control,
  });

  const categoryOptions = getCategoryOptions(contractType);
  const isFullTime = work_schedule === WorkSchedule.FULL_TIME;

  const courseStartAndEndDates = useMemo(() => {
    if (!course || isLoading) {
      return null;
    }

    try {
      return getCourseDatesFromAdminCourseSchedule({
        adminCourseScheduleItems: course.schedule,
        courseSegment: course_segment ?? CourseSegment.FULL_COURSE,
        workSchedule: work_schedule ?? WorkSchedule.FULL_TIME,
        startAt: toUtcStartOfDay(start_at_date, 'YYYY-MM-DD'),
      });
    } catch {
      // Invalid user input for the start date
      return null;
    }
  }, [course, course_segment, isLoading, start_at_date, work_schedule]);

  useEffect(() => {
    if (auth != null && quickReferralModalSnap.enrolmentSettingsData.owned_by === '') {
      const userRole = auth.user.role;
      if (userRole === ConsultantRoleSchema.enum.SITE_MANAGER) {
        setValue('owned_by', auth.user.id);
        quickReferralModalState.enrolmentSettingsData.owned_by = auth.user.id;
      }
    }
  }, [auth, quickReferralModalSnap.enrolmentSettingsData.owned_by, setValue]);

  const sitesOptions: SiteOption[] = useMemo(() => {
    return (
      sitesList
        ?.map((site) => {
          const site_state = StatesSchema.safeParse(site.state).data;
          if (typeof site_state !== 'string') {
            return;
          }

          return {
            value: {
              site_id: site.id,
              site_name: `${site.name}, ${site.state}`,
              site_state,
            },
            label: site.state ? `${site.name}, ${site.state}` : site.name,
          } satisfies SiteOption;
        })
        .filter(isDefined) || ([] satisfies SiteOption[])
    );
  }, [sitesList]);

  const loadSitesOptions = useDebounceCallback<
    (
      inputValue: string,
      callback: (
        sites: {
          value: {
            site_id: string;
            site_name: string;
            site_state: string | undefined;
          };
          label: string;
        }[],
      ) => void,
    ) => void
  >((inputValue: string, callback) => {
    if (sitesOptions.length > 0) {
      callback(
        sitesOptions.filter(
          (option) =>
            option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
            option.value.site_name.toLowerCase().includes(inputValue.toLowerCase()),
        ),
      );

      // If there's only one site available, select it
      if (selected_site.site_id.length === 0 && sitesOptions.length === 1) {
        if (typeof sitesOptions?.[0]?.value?.site_id !== 'undefined') {
          setValue('selected_site', sitesOptions[0].value);
        }
      }
    } else {
      callback([]);
    }
  }, 500);

  const consultantOptions: ConsultantOption[] = useMemo(() => {
    return consultantsInSite
      .map((user) => {
        if (user.role === undefined) {
          return null;
        }
        return {
          role: user.role,
          email: user.email ?? '',
          value: user.id,
          label: `${user.first_name} ${user.last_name} ${
            auth.user.id === user.id ? `(${t`myself`})` : ''
          }`,
          fullname: `${user.first_name} ${user.last_name}`,
        };
      })
      .filter((option): option is ConsultantOption => option !== null);
  }, [auth.user.id, consultantsInSite]);

  const loadConsultantsOptions = useDebounceCallback<
    (inputValue: string, callback: (opts: ConsultantOption[]) => void) => void
  >((inputValue, callback) => {
    if (consultantOptions.length > 0) {
      callback(
        consultantOptions.filter(
          (option) =>
            option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
            option.email?.toLowerCase().includes(inputValue.toLowerCase()),
        ),
      );
    } else {
      callback([]);
    }
  }, 500);

  const handleContractTypeChange = useCallback(
    (val: SingleValue<ContractTypesInterface>) => {
      const selectedContractType = val?.value;
      if (typeof selectedContractType === 'undefined' || typeof selectedContractType !== 'string') {
        return;
      }

      onContractTypeChange(selectedContractType);

      // Default logic for all contract types
      setValue('course_segment', CourseSegment.FULL_COURSE);

      // Overwrite defaults for specific contract types
      if (selectedContractType === 'Workforce Australia') {
        if (courseData.kind === CourseKindSchema.enum.QUALIFICATION) {
          setValue('course_segment', CourseSegment.FIRST_SEMESTER_ONLY);
        }
      } else if (selectedContractType === 'IEA') {
        // IEA requires full-time enrolment pace
        setValue('work_schedule', WorkSchedule.FULL_TIME);
      }

      const newCategoryOptions = getCategoryOptions(selectedContractType);
      // Always reset contract_category based on available options
      if (newCategoryOptions.length === 1 && typeof newCategoryOptions[0]?.value === 'string') {
        setValue('contract_category', newCategoryOptions[0].value);
      } else if (newCategoryOptions.length === 0) {
        setValue('contract_category', 'n/a');
      } else {
        setValue('contract_category', '');
      }
    },
    [onContractTypeChange, setValue, courseData.kind],
  );

  const hasSemesterTwo = useMemo(() => {
    return courseHasSemesterTwo(course?.schedule ?? []);
  }, [course]);

  function onSubmit(values: EnrolmentSettingsDataTypes) {
    if (auth.user.role === ConsultantRoleSchema.enum.CONSULTANT) {
      values.owned_by = auth.user.id;
    }
    quickReferralModalState.setEnrolmentSettingsData(values);
    goToNextStep();
  }

  return (
    <Box
      as="form"
      onSubmit={handleSubmit(onSubmit)}
      style={{ width: '100%', height: '100%' }}
      noValidate
    >
      <FormMainContainer>
        <FormContentContainer>
          <ParticipantExistsAlert />
          <FormControl isInvalid={!!errors.selected_site} isRequired>
            <FormLabel htmlFor="selected_site">
              <Trans>Site</Trans>
            </FormLabel>
            {isSitesLoading ? (
              <Skeleton height="32px" />
            ) : (
              <Controller
                key={quickReferralModalSnap.enrolmentSettingsData.selected_site.site_id}
                name="selected_site"
                control={control}
                rules={{
                  required: t`Please select a site.`,
                  validate: (value) =>
                    typeof value?.site_id === 'string' && value?.site_id.length > 0
                      ? true
                      : t`Please select a site.`,
                }}
                render={({ field }) => (
                  <AsyncSelect
                    id="selected_site"
                    name="selected_site"
                    placeholder={t`Select site`}
                    defaultOptions
                    loadOptions={loadSitesOptions}
                    selectedOptionColorScheme={'brand'}
                    value={sitesOptions?.find(
                      (option) => option.value.site_id === field.value.site_id,
                    )}
                    onChange={(
                      selectedOption:
                        | MultiValue<{
                            value: {
                              site_id: string;
                              site_name: string;
                              site_state: string | undefined;
                            };
                            label: string;
                          }>
                        | SingleValue<{
                            value: {
                              site_id: string;
                              site_name: string;
                              site_state: string | undefined;
                            };
                            label: string;
                          }>,
                    ) => {
                      if (
                        selectedOption != null &&
                        'value' in selectedOption &&
                        selectedOption?.value != null
                      ) {
                        field.onChange(selectedOption?.value);

                        if (
                          sitesOptions.length > 1 &&
                          tenantCtx?.permissions.includes('canCreateReferralsForTeamMembers') ===
                            true
                        ) {
                          setValue('selected_consultant', {
                            consultant_id: '',
                            consultant_name: '',
                          });

                          quickReferralModalState.setConsultant({
                            consultant_id: '',
                            consultant_name: '',
                          });
                        }
                      }
                    }}
                  />
                )}
              />
            )}
            <FormErrorMessage>{errors.selected_site?.message}</FormErrorMessage>
          </FormControl>

          {tenantCtx?.permissions.includes('canCreateReferralsForTeamMembers') === true &&
          selected_site?.site_id.length > 0 ? (
            <FormControl isInvalid={!!errors.selected_consultant} isRequired>
              <FormLabel htmlFor="selected_consultant">
                <Trans>Consultant</Trans>
              </FormLabel>
              {isConsultantsLoading ? (
                <Skeleton height="32px" />
              ) : (
                <Controller
                  key={getValues('selected_site').site_id}
                  name="selected_consultant"
                  control={control}
                  rules={{
                    required: t`Please select a consultant.`,
                    validate: (value) =>
                      value != null && value.consultant_id ? true : t`Please select a consultant.`,
                  }}
                  render={({ field }) => (
                    <AsyncSelect
                      id="selected_consultant"
                      name="selected_consultant"
                      placeholder={t`Select consultant`}
                      defaultOptions
                      loadOptions={loadConsultantsOptions}
                      selectedOptionColorScheme={'brand'}
                      components={{ Option }}
                      value={
                        consultantOptions?.find(
                          (option) => option.value === field.value?.consultant_id,
                        ) ?? null
                      }
                      onChange={(
                        selectedOption:
                          | MultiValue<ConsultantOption>
                          | SingleValue<ConsultantOption>,
                      ) => {
                        if (
                          selectedOption != null &&
                          'value' in selectedOption &&
                          selectedOption?.label != null &&
                          selectedOption?.value != null
                        ) {
                          field.onChange({
                            consultant_id: selectedOption?.value,
                            consultant_name: selectedOption?.label,
                          });
                        }
                      }}
                      defaultValue={consultantOptions.find(
                        (option) => option.value === auth.user.id,
                      )}
                    />
                  )}
                />
              )}
              <FormErrorMessage>{errors.selected_consultant?.message}</FormErrorMessage>
            </FormControl>
          ) : null}

          <HStack w="full" justify="space-between" align="start" gap="10px">
            <FormControl isInvalid={!!errors.start_at_date} isRequired>
              <FormLabel htmlFor="start_at_date">
                <Trans>Start date</Trans>
              </FormLabel>
              <Input
                id="start_at_date"
                min={djs(today).format('YYYY-MM-DD')}
                {...register('start_at_date', {
                  required: t`Start date is required.`,
                  min: {
                    value: djs(today).format('YYYY-MM-DD'),
                    message: t`Please enter a valid start date. The date should not be in the past.`,
                  },
                  validate: (value) => djs(value).isValid() || t`Please enter a valid date.`,
                })}
                type="date"
              />
              <FormErrorMessage>{errors.start_at_date?.message}</FormErrorMessage>
            </FormControl>

            <FormControl
              isInvalid={!!errors.external_id}
              isRequired={tenantCtx?.settings.is_external_id_field_mandatory}
            >
              <FormLabel htmlFor="external_id">
                <Trans>Purchase order</Trans>
              </FormLabel>
              <Input
                id="external_id"
                {...register('external_id', {
                  required:
                    tenantCtx?.settings.is_external_id_field_mandatory === true &&
                    t`This field is required.`,
                  setValueAs: (value: string) => {
                    if (typeof value === 'string') {
                      const trimmed = value.trim();
                      return trimmed === '' ? undefined : trimmed;
                    }
                    return value;
                  },
                })}
                type="text"
                spellCheck="false"
                autoComplete="off"
              />
              <FormErrorMessage>{errors.external_id?.message}</FormErrorMessage>
            </FormControl>
          </HStack>

          <HStack w="full" justify="space-between" align="start" gap="10px">
            <FormControl isInvalid={!!errors.enrolment_contract_type} isRequired>
              <FormLabel htmlFor="enrolment_contract_type">
                <Trans>Contract type</Trans>
              </FormLabel>
              <Controller
                name="enrolment_contract_type"
                control={control}
                rules={{ required: t`Contract type is required.` }}
                render={({ field }) => (
                  <Select
                    id="enrolment_contract_type"
                    placeholder={t`Select option`}
                    options={ContractTypes}
                    value={ContractTypes.find((option) => option.value === field.value)}
                    onChange={handleContractTypeChange}
                  />
                )}
              />
              <FormErrorMessage>{errors.enrolment_contract_type?.message}</FormErrorMessage>
            </FormControl>

            {categoryOptions.length > 0 ? (
              <FormControl
                isInvalid={!!errors.contract_category}
                isRequired
                isDisabled={categoryOptions.length < 1}
              >
                <FormLabel htmlFor="contract_category">
                  <Trans>Contract category</Trans>
                </FormLabel>
                <Controller
                  name="contract_category"
                  control={control}
                  rules={{ required: t`Contract category is required.` }}
                  render={({ field }) => (
                    <Select
                      key={getValues('contract_category')}
                      id="contract_category"
                      placeholder={t`Select option`}
                      options={categoryOptions}
                      value={categoryOptions.find((option) => option.value === field.value)}
                      onChange={(val: SingleValue<CategoryOptionsInterface>) =>
                        field.onChange(val?.value)
                      }
                      isReadOnly={categoryOptions.length === 1}
                    />
                  )}
                />
                <FormErrorMessage>{errors.contract_category?.message}</FormErrorMessage>
              </FormControl>
            ) : (
              <FormControl />
            )}
          </HStack>

          <HStack w="full" justify="space-between" align="start" gap="10px">
            {hasSemesterTwo ? (
              <FormControl isInvalid={!!errors.course_segment} isRequired>
                <FormLabel htmlFor="course_segment">
                  <Trans>Course segment</Trans>
                </FormLabel>
                <Controller
                  name="course_segment"
                  control={control}
                  rules={{ required: t`Course segment is required.` }}
                  render={({ field }) => (
                    <Select
                      id="course_segment"
                      placeholder={t`Select option`}
                      options={CourseSegmentOptions}
                      value={CourseSegmentOptions.find((option) => option.value === field.value)}
                      isDisabled={enrolment_contract_type === 'IEA'}
                      onChange={(val: SingleValue<CourseSegmentOptionsInterface>) =>
                        field.onChange(val?.value)
                      }
                    />
                  )}
                />
                <FormErrorMessage>{errors.course_segment?.message}</FormErrorMessage>
              </FormControl>
            ) : null}

            <FormControl isRequired>
              <FormLabel>
                <Trans>Enrolment pace</Trans>
              </FormLabel>
              <HStack align="center">
                <ButtonGroup spacing={0} isAttached>
                  <Button
                    colorScheme={isFullTime ? 'brand' : 'gray'}
                    onClick={() => setValue('work_schedule', WorkSchedule.FULL_TIME)}
                    variant={isFullTime ? 'solid' : 'ghost'}
                    isDisabled={
                      courseData.kind === CourseKindSchema.enum.QUALIFICATION &&
                      (enrolment_contract_type === 'IEA' || !enrolment_contract_type)
                    }
                  >
                    <Trans>Full-time</Trans>
                  </Button>
                  <Button
                    colorScheme={!isFullTime ? 'brand' : 'gray'}
                    onClick={() => setValue('work_schedule', WorkSchedule.PART_TIME)}
                    variant={!isFullTime ? 'solid' : 'ghost'}
                    isDisabled={
                      courseData.kind === CourseKindSchema.enum.QUALIFICATION &&
                      (enrolment_contract_type === 'IEA' || !enrolment_contract_type)
                    }
                  >
                    <Trans>Part-time</Trans>
                  </Button>
                </ButtonGroup>
                {!isFullTime && (
                  <Tooltip
                    label={
                      <Box
                        bg="bg.surface"
                        borderRadius="8px"
                        color="fg.default"
                        borderWidth="1px"
                        borderColor="border.default"
                        p="8px"
                        fontSize="sm"
                      >
                        <Trans>
                          Please note, by selecting part-time no outcome can be claimed.
                        </Trans>
                      </Box>
                    }
                    fontSize="md"
                    colorScheme="warning"
                    placement="bottom"
                    gutter={16}
                    bg="transparent"
                    shadow="none"
                  >
                    <Icon as={AlertCircleIcon} color="warning.emphasis" boxSize="20px" />
                  </Tooltip>
                )}
              </HStack>
            </FormControl>
          </HStack>

          {courseStartAndEndDates?.courseEndDate ? (
            <Text color="fg.emphasis" textStyle="sm" textAlign="left" fontWeight="normal" w="full">
              <Trans>Course is expected to end by</Trans>{' '}
              {courseStartAndEndDates?.courseEndDate.long}
            </Text>
          ) : null}
        </FormContentContainer>
        <FormFooterContainer>
          <Button rightIcon={<ChevronRightIcon />} isLoading={isSubmitting} type="submit">
            <Trans>Next</Trans>
          </Button>
          <Button onClick={goToPrevStep} variant="tertiary" leftIcon={<ChevronLeftIcon />}>
            <Trans>Back</Trans>
          </Button>
        </FormFooterContainer>
      </FormMainContainer>
    </Box>
  );
}
